<c-container fluid>
  <!-- The raw token, shown exactly once -->
  @if (newToken(); as token) {
    <div class="alert alert-success">
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <strong>{{ 'partner_tokens.new_token_title' | translate }}</strong>
          <div class="small">{{ 'partner_tokens.new_token_hint' | translate }}</div>
        </div>
        <button cButton color="secondary" variant="outline" size="sm" (click)="dismissToken()">
          {{ 'partner_tokens.dismiss' | translate }}
        </button>
      </div>
      <div class="token-box mt-2">
        <code>{{ token }}</code>
        <button cButton color="primary" size="sm" (click)="copyToken()">
          {{ (copied() ? 'partner_tokens.copied' : 'partner_tokens.copy') | translate }}
        </button>
      </div>
    </div>
  }

  @if (error()) {
    <div class="alert alert-danger py-2">{{ error() }}</div>
  }

  <c-card class="mb-4">
    <c-card-header class="d-flex justify-content-between align-items-center">
      <div>
        <strong>{{ 'partner_tokens.title' | translate }}</strong>
        <div class="small text-body-secondary">{{ 'partner_tokens.subtitle' | translate }}</div>
      </div>
      <button cButton color="primary" (click)="toggleForm()">
        {{ (showForm() ? 'partner_tokens.cancel' : 'partner_tokens.new_token') | translate }}
      </button>
    </c-card-header>

    @if (showForm()) {
      <c-card-body class="border-bottom">
        <c-row class="mb-3">
          <c-col xs="12" md="3">
            <label cLabel for="formPartner">{{ 'partner_tokens.field_partner' | translate }}</label>
            <input cFormControl id="formPartner" type="text"
                   [ngModel]="formPartner()" (ngModelChange)="formPartner.set($event)" />
          </c-col>
          <c-col xs="12" md="5">
            <label cLabel for="formDescription">{{ 'partner_tokens.field_description' | translate }}</label>
            <input cFormControl id="formDescription" type="text"
                   [ngModel]="formDescription()" (ngModelChange)="formDescription.set($event)"
                   [placeholder]="'partner_tokens.description_placeholder' | translate" />
          </c-col>
          <c-col xs="12" md="4">
            <label cLabel for="formExpiresAt">{{ 'partner_tokens.field_expires' | translate }}</label>
            <input cFormControl id="formExpiresAt" type="date"
                   [ngModel]="formExpiresAt()" (ngModelChange)="formExpiresAt.set($event)" />
            <div class="form-text">{{ 'partner_tokens.expires_hint' | translate }}</div>
          </c-col>
        </c-row>

        <c-row class="mb-3">
          <c-col xs="12">
            <label cLabel>{{ 'partner_tokens.field_scopes' | translate }}</label>
            <div class="d-flex flex-wrap gap-3">
              @for (scope of allScopes; track scope) {
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" [id]="'scope-' + scope"
                         [checked]="hasScope(scope)" (change)="toggleScope(scope)" />
                  <label class="form-check-label" [for]="'scope-' + scope">
                    <code>{{ scope }}</code>
                    <span class="text-body-secondary ms-1">{{ scopeLabelKey(scope) | translate }}</span>
                  </label>
                </div>
              }
            </div>
          </c-col>
        </c-row>

        <button cButton color="success" [disabled]="creating()" (click)="create()">
          {{ (creating() ? 'partner_tokens.creating' : 'partner_tokens.create') | translate }}
        </button>
      </c-card-body>
    }

    <c-card-body>
      @if (loading()) {
        <div class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ 'partner_tokens.loading' | translate }}</span>
          </div>
        </div>
      } @else {
        @if (tokens().length > 0) {
          <div class="table-responsive">
            <table cTable hover striped>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{{ 'partner_tokens.col_partner' | translate }}</th>
                  <th>{{ 'partner_tokens.col_fingerprint' | translate }}</th>
                  <th>{{ 'partner_tokens.col_scopes' | translate }}</th>
                  <th>{{ 'partner_tokens.col_status' | translate }}</th>
                  <th>{{ 'partner_tokens.col_expires' | translate }}</th>
                  <th>{{ 'partner_tokens.col_last_used' | translate }}</th>
                  <th>{{ 'partner_tokens.col_created' | translate }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                @for (token of tokens(); track token.id) {
                  <tr [class.opacity-50]="!token.active">
                    <td>{{ token.id }}</td>
                    <td>
                      <div>{{ token.partner }}</div>
                      @if (token.description) {
                        <small class="text-body-secondary">{{ token.description }}</small>
                      }
                    </td>
                    <td><code>{{ token.fingerprint }}</code></td>
                    <td>
                      @for (scope of token.scopes; track scope) {
                        <c-badge color="info" class="me-1">{{ scope }}</c-badge>
                      }
                    </td>
                    <td>
                      <c-badge [color]="statusColor(token)">{{ statusKey(token) | translate }}</c-badge>
                    </td>
                    <td>{{ formatDate(token.expires_at) }}</td>
                    <td>
                      @if (token.last_used_at) {
                        {{ formatDate(token.last_used_at) }}
                      } @else {
                        <span class="text-body-secondary">{{ 'partner_tokens.never_used' | translate }}</span>
                      }
                    </td>
                    <td>
                      <div>{{ formatDate(token.created_at) }}</div>
                      @if (token.created_by_email) {
                        <small class="text-body-secondary">{{ token.created_by_email }}</small>
                      }
                    </td>
                    <td class="text-end">
                      @if (!token.revoked_at) {
                        <button cButton color="danger" variant="outline" size="sm" (click)="revoke(token)">
                          {{ 'partner_tokens.revoke' | translate }}
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="text-center py-4">
            <p class="text-body-secondary">{{ 'partner_tokens.no_tokens' | translate }}</p>
          </div>
        }
      }
    </c-card-body>
  </c-card>
</c-container>
